import { Fragment } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Box, Breadcrumbs, Button, Divider, Link, Snackbar } from '@mui/material';
import { z } from 'zod';

import { PageHeading } from '../../components/PageHeading';
import { useScanTypeMutation } from '../../services/scan-types/mutations';
import { useScanTypeDetailsSuspense } from '../../services/scan-types/queries';
import { BoxType, GroupType, ScanTypeStatus } from '../../services/scan-types/schemas';
import type { AdminUser, SiteAdminUser } from '../../types/User';
import { GeneralInformationForm } from './GeneralInformationForm';
import { canEditForm, createPayload, getDefaultFormValues } from './ScanTypeDetails.utils';

const FormDataSchema = z.object({
    name: z.string().min(1, { message: 'Name is required' }),
    groupType: z.nativeEnum(GroupType),
    status: z.nativeEnum(ScanTypeStatus),
    boxType: z.nativeEnum(BoxType),
    allowFailedChecks: z.boolean(),
    isProtected: z.boolean(),
    description: z.string(),
    createdTime: z.string(),
    createdByUsername: z.string()
});

export type ScanTypeDetailsFormData = z.infer<typeof FormDataSchema>;

const Form = styled('form')`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

type ScanTypeDetailsProps = {
    scanTypeId: number;
    user: AdminUser | SiteAdminUser;
};

export const ScanTypeDetails = ({ scanTypeId, user }: ScanTypeDetailsProps) => {
    const scanTypeDetails = useScanTypeDetailsSuspense(scanTypeId);

    const form = useForm<ScanTypeDetailsFormData>({
        defaultValues: getDefaultFormValues(scanTypeDetails.data),
        resolver: zodResolver(FormDataSchema)
    });

    const scanTypeMutation = useScanTypeMutation(scanTypeId, {
        onSuccess: (data) => {
            form.reset(getDefaultFormValues(data));
        }
    });

    const isFormEnabled = canEditForm(scanTypeDetails.data.isProtected, user.type);

    const onSubmit = (data: ScanTypeDetailsFormData) => {
        if (!isFormEnabled) return;

        const dirtyFieldKeys = Object.keys(form.formState.dirtyFields);

        const payload = createPayload(data, dirtyFieldKeys);

        scanTypeMutation.mutate(payload);
    };

    return (
        <Fragment>
            <FormProvider {...form}>
                <Form onSubmit={form.handleSubmit(onSubmit)}>
                    <Breadcrumbs separator={<ChevronRightIcon />}>
                        <Link component={RouterLink} to="/scans/types" underline="hover" color="inherit">
                            <PageHeading>Scan Types</PageHeading>
                        </Link>

                        <Link
                            component={RouterLink}
                            to={`/scans/types/${scanTypeDetails.data.id}`}
                            underline="hover"
                            color="inherit"
                        >
                            <PageHeading>{scanTypeDetails.data.name}</PageHeading>
                        </Link>
                    </Breadcrumbs>

                    <Divider />

                    <GeneralInformationForm
                        isEnabled={isFormEnabled}
                        canEditField={{
                            isProtected: user.type === 'ADMIN' // Only admins can edit the isProtected field
                        }}
                    />

                    {isFormEnabled && (
                        <Fragment>
                            <Divider />

                            <Box display="flex" flexDirection="row" gap="244px">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => form.reset()}
                                    disabled={!form.formState.isDirty}
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={!form.formState.isDirty || scanTypeMutation.isPending}
                                    startIcon={<SaveIcon />}
                                >
                                    Save
                                </Button>
                            </Box>
                        </Fragment>
                    )}
                </Form>
            </FormProvider>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={scanTypeMutation.isSuccess}
                autoHideDuration={10_000}
                onClose={scanTypeMutation.reset}
                ClickAwayListenerProps={{ mouseEvent: false }}
            >
                <Alert severity="success" onClose={scanTypeMutation.reset}>
                    Scan type updated successfully
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={scanTypeMutation.isError}
                autoHideDuration={10_000}
                color="error"
                onClose={scanTypeMutation.reset}
                ClickAwayListenerProps={{ mouseEvent: false }}
            >
                <Alert severity="error" onClose={scanTypeMutation.reset}>
                    Failed to update scan type: {scanTypeMutation.error?.message}
                </Alert>
            </Snackbar>
        </Fragment>
    );
};
